import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { moduleApi } from '../services/softwareFactoryApi';
import type {
  ListModulesParams,
  SfModule,
  SfModuleCreate,
  SfModuleListResponse,
  SfModuleUpdate,
} from '../types';

export const moduleKeys = {
  all: ['sdevModules'] as const,
  list: (params?: ListModulesParams) => [...moduleKeys.all, 'list', params] as const,
  detail: (id: string) => [...moduleKeys.all, 'detail', id] as const,
};

export function useModules(params?: ListModulesParams) {
  return useQuery({
    queryKey: moduleKeys.list(params),
    queryFn: () => moduleApi.list(params),
  });
}

export function useModule(id: string) {
  return useQuery({
    queryKey: moduleKeys.detail(id),
    queryFn: () => moduleApi.get(id),
    enabled: !!id,
  });
}

export function useCreateModule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: SfModuleCreate) => moduleApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: moduleKeys.all });
    },
  });
}

export function useUpdateModule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SfModuleUpdate }) => moduleApi.update(id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: moduleKeys.all });
      queryClient.invalidateQueries({ queryKey: moduleKeys.detail(variables.id) });
    },
  });
}

export function useDeleteModule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => moduleApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: moduleKeys.all });
    },
  });
}

export function useReorderModules() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (moduleIds: string[]) => moduleApi.reorder(moduleIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: moduleKeys.all });
    },
  });
}

export { type SfModule, type SfModuleListResponse };
